<template>
  <div class="room-detail-container">
    <h1>{{ t('room_detail.title') }}</h1>
    <div
      v-if="roomStore.currentRoom && roomStore.currentRoom.room_players && roomStore.currentRoom.room_players.length > 0"
      class="room-info-card">
      <p><strong>{{ t('room_detail.title') }}:</strong> {{ roomStore.currentRoom.name }}</p>

      <div v-if="roomStore.isHost" class="game-mode-selector">
        <strong>{{ t('room_create.game_mode_label') }}:</strong>
        <select :value="roomStore.currentRoom.room_type" @change="handleGameModeChange">
          <option value="mix">{{ t('room_create.game_mode_mix') }}</option>
          <option value="zoom_out">{{ t('room_create.game_mode_zoom_out') }}</option>
          <option value="resolution">{{ t('room_create.game_mode_resolution') }}</option>
          <option value="noise">{{ t('room_create.game_mode_noise') }}</option>
        </select>
      </div>
      <p v-else><strong>{{ t('room_create.game_mode_label') }}:</strong> {{ t('room_create.game_mode_' +
        roomStore.currentRoom.room_type) }}</p>

      <p><strong>{{ t('room_detail.status') }}:</strong> {{ roomStore.currentRoom.is_public ? t('room_detail.public') :
        t('room_detail.private') }}</p>
      <p>
        <strong>{{ t('room_list.players') }}:</strong>
        {{ roomStore.currentRoom && roomStore.currentRoom.room_players ? roomStore.currentRoom.room_players.length : 0
        }} / {{ roomStore.currentRoom.max_players }}
      </p>

      <h2>{{ t('room_detail.participants_title') }}</h2>
      <ul class="player-list" v-if="roomStore.currentRoom && roomStore.currentRoom.room_players">
        <li v-for="player in roomStore.currentRoom.room_players" :key="player.id"
          :class="{ 'current-user': authStore.user && player.player.id === authStore.user.id }">
          {{ player.player_name || player.player.username }}
          <span v-if="authStore.user && player.player.id === authStore.user.id" class="you-badge">{{
            t('room_detail.you') }}</span>
          <span v-if="player.player.id === roomStore.currentRoom.host.id" class="host-badge">{{
            t('room_detail.owner_tag') }}</span>
        </li>
      </ul>

      <div class="room-actions">
        <!-- 部屋にまだ参加しておらず、ホストでもない場合のみ「部屋に参加する」ボタンを表示 -->
        <div v-if="!isPlayerInRoom && !roomStore.isHost" class="join-form">
          <input v-model="playerNameInput" :placeholder="t('room_list.player_name_placeholder')" class="name-input" />
          <button @click="handleJoinRoom" :disabled="roomStore.isLoading || !playerNameInput" class="join-button">
            {{ t('room_detail.join_room') }}
          </button>
        </div>

        <!-- 参加済みでホストでない場合は「退出する」ボタンを表示 -->
        <button v-if="isPlayerInRoom && !roomStore.isHost" @click="handleLeaveRoom" :disabled="roomStore.isLoading"
          class="leave-button">
          {{ t('room_detail.leave_room') }}
        </button>

        <!-- ホストの場合のみ「ゲームを開始する」ボタンを表示 -->
        <button v-if="roomStore.isHost" @click="handleStartGame"
          :disabled="roomStore.isLoading || (roomStore.currentRoom?.room_players?.length ?? 0) < 2"
          class="start-game-button">
          {{ t('room_detail.start_game') }} ({{ roomStore.currentRoom?.room_players?.length ?? 0 }}/{{
            roomStore.currentRoom.max_players
          }})
        </button>

        <!-- 部屋一覧に戻るボタンをここに追加 -->
        <button @click="goBackToRoomList" class="back-button">
          {{ t('room_detail.back_to_list') }}
        </button>
      </div>

      <div v-if="isPlayerInRoom && roomStore.currentRoom && roomStore.currentRoom.invite_code" class="invite-section">
        <h3>{{ t('room_detail.invite_info') }}</h3>
        <p><strong>{{ t('room_create.invite_code_message') }}</strong> <span class="invite-code">{{
          roomStore.currentRoom.invite_code }}</span></p>
        <div class="share-link">
          <input type="text" :value="shareLink" readonly />
          <button @click="copyLink">{{ t('room_detail.copy_link') }}</button>
        </div>
        <ShareButtons :share-url="shareLink" :share-text="shareInviteText" :hash-tags="hashTags" />
        <div class="qr-code">
          <img :src="qrCodeUrl" alt="QR Code" />
        </div>
      </div>
    </div>
    <div v-else-if="roomStore.isLoading">
      <p>{{ t('common.loading') }}</p>
    </div>
    <div
      v-else-if="roomStore.error || !roomStore.currentRoom || (roomStore.currentRoom.room_players && roomStore.currentRoom.room_players.length === 0)">
      <!-- エラー、部屋なし、または参加者0人の場合 -->
      <div class="room-info-card error-state">
        <p class="error-message">
          {{ roomStore.error?.response?.status === 404 || !roomStore.currentRoom || (roomStore.currentRoom.room_players
            && roomStore.currentRoom.room_players.length === 0)
            ? t('room_detail.room_not_found')
            : t('room_detail.fetch_error') + ': ' + roomStore.error?.message }}
        </p>
        <div class="room-actions">
          <button @click="goBackToRoomList" class="back-button">
            {{ t('room_detail.back_to_list') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onBeforeUnmount, computed, watch, ref } from 'vue'
import { useRoomStore } from '@/stores/room'
import { useAuthStore } from '@/stores/auth' // Authストアをインポート
import { useRouter } from 'vue-router' // 追加
import { useGameStore } from '@/stores/game' // gameStoreをインポート
import ShareButtons from '@/components/ShareButtons.vue';
import { useI18n } from 'vue-i18n';

const props = defineProps<{
  roomId: string
}>()

const { t } = useI18n();

const roomStore = useRoomStore()
const authStore = useAuthStore()
const gameStore = useGameStore() // gameStoreをインスタンス化！ここに追加するよ！
const router = useRouter()


const playerNameInput = ref('')

const isPlayerInRoom = computed(() => {
  return roomStore.isPlayerInRoom;
})

const shareLink = computed(() => window.location.href)
const qrCodeUrl = computed(() => `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(shareLink.value)}`)
const hashTags = ['ズームアウトゲーム', 'マルチプレイ', '招待'];

const shareInviteText = computed(() => {
  const code = roomStore.currentRoom?.invite_code || '';
  return `ズームアウトゲームで遊びませんか？ 招待コード: ${code}`;
});


const copyLink = async () => {
  try {
    await navigator.clipboard.writeText(shareLink.value)
    alert('リンクをコピーしました！')
  } catch (e) {
    console.error(e)
    alert('コピーに失敗しました')
  }
}

// 部屋に参加する関数
const handleJoinRoom = async () => {
  try {
    await roomStore.joinRoom(props.roomId, playerNameInput.value)
    // 参加後に最新の部屋情報を取得（これで isPlayerInRoom が更新される）
    await roomStore.fetchRoomDetail(props.roomId)
    // 念のためWebSocketでも再参加通知を送る（既に接続済みのはずだが）
    roomStore.joinRoomChannel(props.roomId)
    console.log('Joined room!')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error('Failed to join room:', error)

    let message = t('room_detail.join_error');

    // バックエンドからの詳細なエラーメッセージがあれば表示
    if (error.response && error.response.data) {
      const data = error.response.data;
      if (data.error) {
        // "部屋は満員です。" や "ゲームが既に開始されているか..." など
        message = data.error;
      } else if (data.detail) {
        // "部屋が見つかりません。" や DRFの標準エラーなど
        message = data.detail;
      }
    }

    alert(message)
  }
}

const handleLeaveRoom = async () => {
  roomStore.leaveRoomChannel()
  // 退出後、部屋の情報を再取得して表示を更新する
  // 少し待機してから取得しないと、直後の取得では反映されていない可能性がある（非同期処理のため）
  setTimeout(async () => {
    try {
      await roomStore.fetchRoomDetail(props.roomId)
    } catch {
      // 部屋が削除された場合などはエラーになるため、一覧に戻る
      console.log('Room might be deleted or unavailable, redirecting to list.')
      router.push({ name: 'RoomList' })
    }
  }, 500)
}

// 部屋一覧に戻る関数
const goBackToRoomList = () => {
  roomStore.leaveRoomChannel() // 部屋から退出 (WebSocket接続は維持)
  router.push({ name: 'RoomList' })
}

// ゲームを開始する関数 (ホストのみ)
const handleStartGame = () => {
  roomStore.sendGameStart(props.roomId) // roomStoreのアクションを呼び出す
  console.log('ゲーム開始リクエストを送信しました。')
}

// ゲームモード変更 (ホストのみ)
const handleGameModeChange = async (event: Event) => {
  const newType = (event.target as HTMLSelectElement).value;
  try {
    await roomStore.updateRoomMode(props.roomId, newType);
    // 更新成功時、WebSocketで通知がいけばベストだが、APIレスポンスでローカルは更新される
    // 他の参加者には現状通知されないかもしれない（RoomDetailが定期更新またはPush通知をリッスンしてるか要確認）
    // roomStore.tsを見ると、room_updateイベントを受け取ってsetCurrentRoomしている。
    // バックエンド側でupdate時にWebSocket通知を送っているか？ => RoomViewSetのupdateで通知してないかも。
    // 通知がないと他の人には反映されない。
    // views.pyを確認しても通知コードはない。
    // しょうがないので、ここでは「ホストが見ている画面」は変わる。
    // 他の人は、Start Gameされたときにサーバーが正しいroom_typeを使うので問題はないが、表示が古いままになるかも。
    // UX向上のため、WebSocketメッセージで room_update 相当を送るか、自分がホストならリロードさせるか。
    // -> RoomViewSetのpartial_update/updateメソッドをオーバーライドして通知を送るのが筋。
    // とりあえず機能実装優先。
  } catch (error) {
    console.error('Failed to update game mode:', error);
    alert('ゲームモードの変更に失敗しました。');
  }
}

onMounted(async () => {
  try {
    // まず部屋情報を取得
    await roomStore.fetchRoomDetail(props.roomId)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error("Failed to fetch room detail:", error)
    // 404 Not Foundの場合 (部屋が存在しない)
    if (error.response && error.response.status === 404) {
      // ここでは即リダイレクトせず、ストアの状態をクリアまたはエラー表示用に調整
      // ただし、fetchRoomDetail内でerrorがセットされるため、テンプレート側で制御する
      // 部屋が存在しない = 削除されたとみなす
    }
  }

  // WebSocket接続を確立してからチャンネル参加
  try {
    await roomStore.connectGlobalWebSocket(gameStore)
    // 接続完了後に参加リクエスト
    roomStore.joinRoomChannel(props.roomId)
  } catch (wsError) {
    console.error("WebSocket connection failed:", wsError)
  }

  // gameStoreの登録(必要であれば)
  roomStore.registerGameStore(gameStore)
})


onBeforeUnmount(() => {
  // roomStore.disconnectRoomWebSocket() // `goBackToRoomList` で切断するため、ここではコメントアウト
})

// roomStoreのisGameStartedを監視し、trueになったら画面遷移
watch(() => roomStore.isGameStarted, (newValue) => {
  if (newValue) {
    if (roomStore.isPlayerInRoom) {
      console.log('roomStore.isGameStartedがtrueになり、参加者なのでMultiplayerGameViewに遷移します。');
      router.push({ name: 'MultiplayerGame', params: { roomId: props.roomId } });
    } else {
      console.log('roomStore.isGameStartedがtrueになりましたが、参加者ではないため詳細画面にとどまります。');
    }
  }
});
</script>

<style scoped>
.room-detail-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  background-color: #f9f9f9;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

h1 {
  color: #333;
  text-align: center;
  margin-bottom: 20px;
}

.room-info-card {
  background-color: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 20px;
  margin-bottom: 30px;
}

.room-info-card p {
  margin-bottom: 10px;
  font-size: 1.1em;
  color: #555;
}

.room-info-card strong {
  color: #333;
}

h2 {
  color: #444;
  margin-top: 25px;
  margin-bottom: 15px;
  border-bottom: 2px solid #eee;
  padding-bottom: 5px;
}

.player-list {
  list-style-type: none;
  padding: 0;
  margin-bottom: 20px;
}

.player-list li {
  background-color: #eef;
  border: 1px solid #dde;
  padding: 10px 15px;
  margin-bottom: 8px;
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.player-list li span {
  font-weight: bold;
  color: #007bff;
  margin-left: 5px;
}

.player-list li.current-user {
  background-color: #d4edda;
  /* Light green background for you */
  border-color: #c3e6cb;
}

.you-badge {
  color: #28a745 !important;
}

.host-badge {
  color: #007bff;
}

.room-actions {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 30px;
}

.join-button,
.start-game-button {
  padding: 12px 25px;
  font-size: 1.1em;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  color: #fff;
}

.join-button {
  background-color: #28a745;
}

.join-button:hover:not(:disabled) {
  background-color: #218838;
}

.start-game-button {
  background-color: #007bff;
}

.start-game-button:hover:not(:disabled) {
  background-color: #0056b3;
}

.join-button:disabled,
.start-game-button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.error-message {
  color: #dc3545;
  text-align: center;
  font-weight: bold;
}

.back-button {
  padding: 12px 25px;
  font-size: 1.1em;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  color: #fff;
  background-color: #6c757d;
  /* グレー系 */
}

.leave-button {
  background-color: #dc3545;
  /* Red */
  padding: 12px 25px;
  font-size: 1.1em;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  color: #fff;
}

.leave-button:hover:not(:disabled) {
  background-color: #c82333;
}

.leave-button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.back-button:hover:not(:disabled) {
  background-color: #5a6268;
}

.invite-section {
  margin-top: 20px;
  padding: 15px;
  background-color: #f0f0f0;
  border-radius: 6px;
  text-align: center;
}

.invite-code {
  font-size: 1.5em;
  font-weight: bold;
  color: #007bff;
  letter-spacing: 2px;
}

.share-link {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin: 10px 0;
}

.share-link input {
  width: 300px;
  padding: 5px;
}

.share-link button {
  padding: 5px 10px;
  background-color: #17a2b8;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.qr-code img {
  margin-top: 10px;
  border: 1px solid #ddd;
}

.join-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
}

.name-input {
  padding: 10px;
  font-size: 1.1em;
  border: 1px solid #ccc;
  border-radius: 5px;
  width: 200px;
}

.game-mode-selector {
  margin-bottom: 10px;
}

.game-mode-selector select {
  margin-left: 10px;
  padding: 5px;
  border-radius: 4px;
  border: 1px solid #ccc;
  font-size: 1em;
}
</style>
